import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import InfiniteScroll from 'react-infinite-scroll-component';
import { map } from 'lodash';
import Divider from '@material-ui/core/Divider';
import UserItemContainer from '../../containers/UserItemContainer';
import ListLoader from '../../../shared/components/ListLoader/ListLoader';
import CircularLoader from '../../../shared/components/CircularLoader/CircularLoader';
import GeneralError from '../../../shared/components/GeneralError/GeneralError';
import UserListSearchHeaderContainer from '../../containers/UserListSearchHeaderContainer';
import UserListFooterContainer from '../../containers/UserListFooterContainer';
import UserListHeader from '../UserListHeader/UserListHeader';
import styles from './usersList.module.scss';
import './animations.css';

export default memo(function UsersList(props) {
    const [t] = useTranslation('common');

    useEffect(() => {
        props.getUsersRequest(1, 0);

        return () => props.cleanUsers();
    }, []);

    function handleRefresh() {
        props.cleanUsers();
        props.getUsersRequest(1, 0, props.searchText);
    }

    function handleNext() {
        props.getUsersRequest(0, props.users.length, props.searchText);
    }

    return (
        <div className={styles.container}>
            {!props.isFetching && props.error && <GeneralError menu="USERS" message="general_error" />}
            {
                !props.error &&
                <>
                    <UserListSearchHeaderContainer />
                    <div className={styles.listContainer}>
                        <div className={styles.list}>
                            <UserListHeader />
                            <Divider />
                            {
                                props.users.length === 0 &&
                                props.isFetching &&
                                <ListLoader dictionary="users" showProgress text="searching" />
                            }
                            {
                                props.users.length === 0 &&
                                !props.isFetching &&
                                <ListLoader dictionary="users" text="no_users" />
                            }
                            {
                                props.users.length > 0 &&
                                <div className={styles.animatedEntrance} id="scrollableList">
                                    <InfiniteScroll
                                        scrollableTarget="scrollableList"
                                        dataLength={props.users.length}
                                        next={handleNext}
                                        hasMore={props.users.length !== props.total}
                                        loader={
                                            <div className={styles.loaderContainer}>
                                                <CircularLoader size={24} />
                                            </div>
                                        }
                                        refreshFunction={handleRefresh}
                                        pullDownToRefresh
                                        pullDownToRefreshContent={
                                            <div className={styles.refresh}>
                                                <span>&#8595; {t('common:pull_down_to_refresh')}</span>
                                            </div>
                                        }
                                        releaseToRefreshContent={
                                            <div className={styles.refresh}>
                                                <span>&#8593; {t('common:release_to_refresh')}</span>
                                            </div>
                                        }
                                    >
                                        <TransitionGroup>
                                            {map(props.users, (user, index) =>
                                                <CSSTransition component={null} key={user.UserId} timeout={500} classNames="item">
                                                    <>
                                                        <UserItemContainer user={user} />
                                                        {index !== props.users.length - 1 && <Divider />}
                                                    </>
                                                </CSSTransition>
                                            )}
                                        </TransitionGroup>
                                    </InfiniteScroll>
                                </div>
                            }
                        </div>
                        <UserListFooterContainer />
                    </div>
                </>
            }
        </div>
    );
}, (prevProps, nextProps) =>
        prevProps.users === nextProps.users &&
        prevProps.getUsersRequest === nextProps.getUsersRequest &&
        prevProps.cleanUsers === nextProps.cleanUsers &&
        prevProps.isFetching === nextProps.isFetching &&
        prevProps.error === nextProps.error &&
        prevProps.total === nextProps.total
);
